// src/services/userService.js
import { getAllRoles } from './roleService'; // To validate roles

const USERS_STORAGE_KEY = 'loan_servicing_users';

// --- Helper Functions ---
const getUsersFromStorage = () => {
  try {
    const stored = localStorage.getItem(USERS_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) { console.error("Error parsing users:", error); return []; }
};

const saveUsersToStorage = (users) => {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
};

// --- Initialize Dummy Data / Ensure Default Users Exist ---
export
const initializeUsers = () => {
    let users = getUsersFromStorage();
    // Add default users if they don't exist (by username)
    const defaultRoles = getAllRoles(); // Get valid roles
    const defaultUsers = [
         // *** IMPORTANT: Password is NOT stored securely here. DEMO ONLY. ***
        { id: 'usr_admin', username: 'admin', password: 'password', role: 'admin', name: 'Admin User', isActive: true },
        { id: 'usr_manager', username: 'manager', password: 'password', role: 'manager', name: 'Manager User', isActive: true },
        { id: 'usr_staff', username: 'staff', password: 'password', role: 'staff', name: 'Staff User', isActive: true },
        { id: 'usr_accountant', username: 'accountant', password: 'password', role: 'accountant', name: 'Accountant User', isActive: true },
    ];

    let needsSave = false;
    defaultUsers.forEach(defaultUser => {
        // Check if role is valid before adding
        if (!defaultRoles.find(r => r.id === defaultUser.role)) {
             console.warn(`Skipping default user '${defaultUser.username}' due to invalid role '${defaultUser.role}'`);
             return;
        }
        // Add if username doesn't exist
        if (!users.some(u => u.username === defaultUser.username)) {
            console.log(`Adding default user: ${defaultUser.username}`);
            users.push(defaultUser);
            needsSave = true;
        }
         // Optional: Ensure existing default users have correct default password/active status if needed
         const existingUser = users.find(u => u.username === defaultUser.username);
         if (existingUser && (!existingUser.password || existingUser.isActive === undefined)) {
             // existingUser.password = 'password'; // Reset password? Be careful.
             existingUser.isActive = existingUser.isActive ?? true; // Ensure active flag
             needsSave = true;
         }

    });

    if (needsSave) {
        saveUsersToStorage(users);
    }
};
// *** IMPORTANT: Call initialization after defining roles ***
// initializeUsers(); // We'll call this after roleService is defined

// --- CRUD Functions ---
export const getAllUsers = async () => {
  await new Promise(resolve => setTimeout(resolve, 50));
  // Exclude password from data returned to UI
  return getUsersFromStorage().map(({ password, ...user }) => user);
};

export const getUserByUsername = async (username) => {
    await new Promise(resolve => setTimeout(resolve, 20));
    const users = getUsersFromStorage();
    // Find user, DO include password for login check
    return users.find(u => u.username === username && u.isActive === true) || null;
};


export const getUserById = async (id) => {
  await new Promise(resolve => setTimeout(resolve, 50));
  const users = getUsersFromStorage();
  const user = users.find(u => u.id === id);
  if (!user) return null;
  // Exclude password
  const { password, ...userData } = user;
  return userData;
};

export const createUser = async (userData) => {
  // userData expects: username, name, role, isActive
  await new Promise(resolve => setTimeout(resolve, 100));
  const users = getUsersFromStorage();

  // Validate username uniqueness
  if (users.some(u => u.username.toLowerCase() === userData.username.toLowerCase())) {
    throw new Error(`Username '${userData.username}' already exists.`);
  }
  // Validate role
  if (!getAllRoles().find(r => r.id === userData.role)) {
      throw new Error(`Invalid role selected: ${userData.role}`);
  }

  const newUser = {
    id: `usr_${Date.now()}`,
    // *** Set a default password - NEVER do this in production ***
    password: 'password', // Or prompt admin, or send reset link (complex)
    isActive: userData.isActive === true, // Ensure boolean
    ...userData, // username, name, role
  };
  users.push(newUser);
  saveUsersToStorage(users);
  // Exclude password before returning
  const { password, ...createdUser } = newUser;
  return createdUser;
};

export const updateUser = async (id, updatedData) => {
  // updatedData expects: name, role, isActive. Username/Password not changed here.
  await new Promise(resolve => setTimeout(resolve, 100));
  let users = getUsersFromStorage();
  const index = users.findIndex(u => u.id === id);
  if (index > -1) {
    // Validate role if changed
    if (updatedData.role && !getAllRoles().find(r => r.id === updatedData.role)) {
        throw new Error(`Invalid role selected: ${updatedData.role}`);
    }
    // Merge changes, keep existing password and username
    users[index] = {
        ...users[index],
        name: updatedData.name ?? users[index].name,
        role: updatedData.role ?? users[index].role,
        isActive: updatedData.isActive !== undefined ? updatedData.isActive === true : users[index].isActive,
    };
    saveUsersToStorage(users);
    // Exclude password before returning
    const { password, ...savedUser } = users[index];
    return savedUser;
  }
  return null; // User not found
};

export const deleteUser = async (id, currentUserId) => {
    await new Promise(resolve => setTimeout(resolve, 100));
    if (id === currentUserId) {
        throw new Error("Cannot delete the currently logged-in user.");
    }
    let users = getUsersFromStorage();
    const userToDelete = users.find(u => u.id === id);
    // Prevent deleting the primary admin? (Optional business rule)
    // if (userToDelete?.username === 'admin') {
    //    throw new Error("Cannot delete the primary admin user.");
    // }

    const updatedUsers = users.filter(u => u.id !== id);
    if (users.length !== updatedUsers.length) {
        saveUsersToStorage(updatedUsers);
        return true;
    }
    return false; // User not found
};